<template>
  <div>
    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay d-flex justify-content-center align-items-center">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <span class="ml-2">Loading dashboard...</span>
    </div>

    <!-- PROFIT SUMMARY -->
    <div class="row" v-else>
      <div class="alert alert-success text-center col-sm-6">
        <h6>Today: Ksh. {{ formatPrice(profit_today) }}</h6>
      </div>
      <div class="alert alert-primary text-center col-sm-6">
        <h6>Total: Ksh. {{ formatPrice(profit_total) }}</h6>
      </div>
    </div>

    <!-- CARDS -->
    <div class="row" v-if="!loading">
      <div class="col-xl-3 col-lg-6 col-sm-6 grid-margin stretch-card" v-for="card in cards" :key="card.title">
        <div class="card">
          <div class="card-body text-center">
            <h4 class="mb-4 text-dark font-weight-bold">{{ card.title }}</h4>
            <div class="dashboard-progress d-flex align-items-center justify-content-center">
              <img :src="card.img" style="width: 20%;" :alt="card.title" />
            </div>
            <h3 class="mb-0 mt-5 font-weight-bold text-dark">
              {{ card.prefix }}{{ formatPrice(card.value) }}
            </h3>
          </div>
        </div>
      </div>
    </div>

    <!-- CHARTS -->
    <div class="row justify-content-center mt-4" v-if="!loading">
      <div class="col-sm-6">
        <div class="card">
          <div class="card-body">
            <apexchart type="pie" width="380" :options="chartOptions" :series="series" />
          </div>
        </div>
      </div>
      <div class="col-sm-6">
        <div class="card">
          <div class="card-body">
            <apexchart type="pie" width="380" :options="chartOptions3" :series="series3" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "Dashboard",
  data() {
    return {
      loading: true,
      report: {},
      discounts: {},
      profit_today: 0,
      profit_total: 0,
      series: [],
      series3: [],
      chartOptions: {
        labels: ["Safaricom Airtime", "Other Airtime"]
      },
      chartOptions3: {
        labels: ["Airtime Received", "Airtime Sold"]
      }
    };
  },
  computed: {
    cards() {
      return [
        {
          title: "Airtime Sold Today",
          value: this.safe(this.report.sent_today),
          img: "/images/saf.jpeg",
          prefix: "Ksh. "
        },
        {
          title: "Airtime Converted Today",
          value: this.safe(this.report.received_today),
          img: "/images/mpesa.png",
          prefix: "Ksh. "
        },
        {
          title: "Safaricom Airtime Sold",
          value: this.safe(this.report.saf),
          img: "/images/saf.jpeg",
          prefix: "Ksh. "
        },
        {
          title: "Other Airtime Sold",
          value: this.safe(this.report.other),
          img: "/images/airtel2.png",
          prefix: "Ksh. "
        },
        {
          title: "All Airtime Converted",
          value: this.safe(this.report.received_total),
          img: "/images/mpesa2.png",
          prefix: "Ksh. "
        },
        {
          title: "Customers Converting",
          value: this.safe(this.report.converting),
          img: "/assets/img/google.png",
          prefix: ""
        },
        {
          title: "Customers Purchasing",
          value: this.safe(this.report.purchasing),
          img: "/assets/img/login.jpg",
          prefix: ""
        },
        {
          title: "B2C Paybill Balance",
          value: this.safe(this.report?.b2cBalance?.balance),
          img: "/images/mpesa2.png",
          prefix: "Ksh. "
        }
      ];
    }
  },
  created() {
    console.log("🚀 Dashboard created");
    this.loadDashboard();
  },
  methods: {
    safe(val) {
      return val ? Number(val) : 0;
    },
    async loadDashboard() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        console.log("📡 Fetching dashboard...");
        const [dashboardResp, discountResp] = await Promise.all([
          this.axios.get("/api/auth/dashboard", {
            headers: { Authorization: "Bearer " + token }
          }),
          this.axios.get("/api/auth/discount", {
            headers: { Authorization: "Bearer " + token }
          })
        ]);

        console.log("✅ Dashboard response:", dashboardResp.data);
        console.log("✅ Discount response:", discountResp.data);

        this.report = dashboardResp.data || {};
        this.discounts = discountResp.data || {};

        // Charts
        this.series = [
          this.safe(this.report.saf),
          this.safe(this.report.other)
        ];
        this.series3 = [
          this.safe(this.report.received_total),
          this.safe(this.report.sold_total)
        ];

        // Calculate profits
        this.calculate();
      } catch (error) {
        console.error("❌ Dashboard load ERROR:", error);
        if (error.response?.status === 401) {
          this.$router.push({ name: "index" });
          this.$toast.open({ message: "Please sign in!", type: "error" });
        } else {
          this.$toast.open({
            message: error.response?.data?.message || "Dashboard failed",
            type: "error"
          });
        }
      } finally {
        this.loading = false;
      }
    },
    calculate() {
      console.log("🧮 Calculating profits...");
      const airtimeRate = this.safe(this.discounts?.airtime?.rate);
      const mpesaRate = this.safe(this.discounts?.mpesa?.rate);
      console.log("Rates:", airtimeRate, mpesaRate);

      const sentToday = this.safe(this.report.sent_today);
      const saf = this.safe(this.report.saf);

      const sold_today = (sentToday * (100 - airtimeRate)) / 100;
      const bought_today = (sentToday * (100 - mpesaRate)) / 100;
      this.profit_today = sold_today - bought_today;

      const sold = (saf * (100 - airtimeRate)) / 100;
      const bought = (saf * (100 - mpesaRate)) / 100;
      this.profit_total = sold - bought;

      console.log("💰 Profit today:", this.profit_today);
      console.log("💰 Profit total:", this.profit_total);
    },
    formatPrice(value) {
      let val = (value / 1).toFixed(2);
      return val.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    filterDate(date) {
      return moment(String(date)).format("MM/DD/YYYY HH:mm");
    }
  }
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.85);
  z-index: 9999;
  flex-direction: row;
  font-weight: bold;
  font-size: 1.2rem;
}
</style>